import { useTranslation } from "react-i18next";
import Button from "@imminently/decisively-ui/Button";
import SelectField from "@imminently/decisively-ui/SelectField";
import SwitchField from "@imminently/decisively-ui/SwitchField";
import Icon from "@imminently/decisively-ui/Icon";
import { useIsSuperAdmin } from "@/auth/permissions";
import TextField from "@imminently/decisively-ui/TextField";
import { FieldWatch, useField } from "@imminently/decisively-ui/Form";
import { Label } from "@/ui/primitives/label";
import { UserCombobox } from "@/components/radix/UserSelect/UserSelect";
import { AssigneeGroup } from "@/ui/forms/assignee-group";
import type { WorkspaceFormValues } from "../types";

interface WorkspaceFormProps {
  isEditing: boolean;
}

export const WorkspaceForm = ({ isEditing }: WorkspaceFormProps) => {
  const { t } = useTranslation();
  const usersField = useField<any[]>({ name: "users" });
  const isSuperAdmin = useIsSuperAdmin();

  return (
    <div className="flex flex-col gap-4">
      <TextField
        name="name"
        label={t("workspaces.name")}
        fullWidth
      />

      <TextField
        name="aiKey"
        label={t("workspaces.ai_key")}
        helperText={isEditing ? t("workspaces.ai_key_helper_edit") : t("workspaces.ai_key_helper_create")}
        fullWidth
      />

      <SelectField
        name="visibility"
        label={t("workspaces.visibility")}
        options={[
          { value: "public", label: t("workspaces.visibility_public") },
          { value: "private", label: t("workspaces.visibility_private") },
        ]}
        fullWidth
      />

      {isSuperAdmin && (
        <SwitchField
          name="canaryTestingWorkspace"
          label={
            <span className="inline-flex items-center gap-1.5 align-top">
              <Icon name="bird" />
              {t("workspaces.canary_testing")}
            </span>
          }
          helperText={t("workspaces.canary_testing_workspace_description")}
        />
      )}

      <FieldWatch names={["visibility"]}>
        {({ visibility }) =>
          (visibility as WorkspaceFormValues["visibility"] | undefined) === "private" ? (
            <div className="flex flex-col gap-1.5">
              <Label>{t("workspaces.members")}</Label>
              <UserCombobox
                value={usersField.value ?? []}
                onChange={usersField.onValueChange as any}
                searchProps={{ placeholder: t("search") }}
                popover={{ align: "end" }}
              >
                <Button
                  label={t("workspaces.assignees")}
                  variant="ghost"
                  style={{ width: "100%", justifyContent: "space-between" }}
                >
                  <span>{t("workspaces.assignees")}</span>
                  <AssigneeGroup
                    users={usersField.value ?? []}
                    max={10}
                  />
                </Button>
              </UserCombobox>
            </div>
          ) : null
        }
      </FieldWatch>
    </div>
  );
};
